import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import TcnThemeProvider from '../../components/tcn/ThemeProvider';
import { TCN } from '../../components/tcn/ui-kit/tokens';
import CTAButton from '../../components/tcn/ui-kit/CTAButton';
import Icon from '../../components/tcn/ui-kit/Icon';

/**
 * /tcn/login — TCN-themed dark login (alternative to /login-authentication).
 * Same Supabase Auth flow, same role-based redirects, dark TCN skin.
 *
 * Route to add (in src/Routes.jsx):
 *   <Route path="/tcn/login" element={<TcnLogin />} />
 *   (public — no role guard)
 */

// Map a NoiRock role to its TCN-aware landing route.
const routeForRole = (role) => {
  switch (role) {
    case 'super_admin':
      return '/superadmin';
    case 'tenant_admin':
      return '/superadmin';
    case 'agency_admin':
    case 'agency_owner':
      return '/tcn/admin/upload';
    case 'manager':
    case 'agent':
      return '/tcn/me';
    case 'creator':
      return '/tcn/my/dashboard';
    default:
      return '/tcn/me';
  }
};

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const TcnLogo = () => (
  <div
    style={{
      width: 64,
      height: 64,
      borderRadius: 18,
      background: `linear-gradient(135deg, ${TCN.cyan}, ${TCN.diamond})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#000',
      fontWeight: 800,
      fontSize: 28,
      letterSpacing: '-0.06em',
      boxShadow: `0 0 32px ${TCN.cyan}55`,
    }}
  >
    T
  </div>
);

const TcnLogin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If already signed in, redirect according to role
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || cancelled) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .single();
      if (cancelled) return;
      navigate(routeForRole(profile?.role), { replace: true });
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!email?.trim()) {
      setError(t('login.emailRequired', 'Email required'));
      return;
    }
    if (!password) {
      setError(t('login.passwordRequired', 'Password required'));
      return;
    }

    setLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (authError) {
        setError(authError.message || t('login.invalidCredentials', 'Invalid credentials'));
        setLoading(false);
        return;
      }

      const userId = authData?.user?.id;
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, full_name, tenant_id, agency_id, language')
        .eq('id', userId)
        .single();

      if (profileError || !profile) {
        setError(t('login.profileNotFound', 'Profile not found. Contact your admin.'));
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      navigate(routeForRole(profile.role), { replace: true });
    } catch (err) {
      setError(t('login.networkError', 'Network error. Try again.'));
      setLoading(false);
    }
  };

  return (
    <TcnThemeProvider forceTcn>
      <div
        className="min-h-[100dvh] flex flex-col relative overflow-hidden"
        style={{
          background: TCN.bg,
          color: TCN.text,
          fontFamily: TCN.fontSans,
        }}
      >
        {/* Ambient TCN glow */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div
            className="absolute -top-40 left-1/2 -translate-x-1/2 w-[680px] h-[420px] rounded-full"
            style={{
              background: `radial-gradient(ellipse, ${TCN.cyan}26 0%, transparent 70%)`,
              filter: 'blur(80px)',
            }}
          />
          <div
            className="absolute bottom-0 right-0 w-[420px] h-[320px] rounded-full"
            style={{
              background: `radial-gradient(ellipse, ${TCN.magenta}1f 0%, transparent 70%)`,
              filter: 'blur(80px)',
            }}
          />
        </div>

        {/* Top bar */}
        <header className="relative z-10 flex items-center justify-between px-6 py-5">
          <div className="flex items-center gap-2">
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: `linear-gradient(135deg, ${TCN.cyan}, ${TCN.diamond})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#000',
                fontWeight: 800,
                fontSize: 14,
                letterSpacing: '-0.05em',
              }}
            >
              T
            </div>
            <span style={{ fontWeight: 700, fontSize: 14, letterSpacing: '-0.02em' }}>
              TCN Agent
            </span>
          </div>
          <Link
            to="/login-authentication"
            style={{
              fontSize: 12,
              color: TCN.textDim,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Icon name="arrow-right" size={14} />
            {t('tcn.login.useGeneric', 'Use NoiRock login')}
          </Link>
        </header>

        {/* Centered card */}
        <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-sm">
            <div className="text-center mb-8">
              <div className="inline-flex flex-col items-center gap-4 mb-4">
                <TcnLogo />
                <h1
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    letterSpacing: '-0.04em',
                    color: TCN.text,
                    margin: 0,
                  }}
                >
                  {t('tcn.login.brand', 'TCN Agent')}
                </h1>
              </div>
              <p style={{ color: TCN.textDim, fontSize: 14, lineHeight: 1.5, margin: 0 }}>
                {t('tcn.login.tagline', 'Sign in to your TCN agent app')}
              </p>
            </div>

            <div
              style={{
                background: TCN.surface1,
                border: `1px solid ${TCN.border}`,
                borderRadius: 20,
                padding: 24,
                boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
              }}
            >
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: 10,
                      background: `${TCN.red}1f`,
                      border: `1px solid ${TCN.red}55`,
                      color: TCN.red,
                      fontSize: 13,
                    }}
                  >
                    {error}
                  </div>
                )}

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 11,
                      fontWeight: 600,
                      color: TCN.textDim,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    {t('login.emailLabel', 'Email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@tcn.agency"
                    autoComplete="email"
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: 12,
                      background: TCN.surface2,
                      border: `1px solid ${TCN.border}`,
                      color: TCN.text,
                      // FRM-001/TCH-001 — 16px : pas de zoom iOS au focus.
                      fontSize: 16,
                      fontFamily: TCN.fontSans,
                      outline: 'none',
                      transition: `border-color ${TCN.durBase} ${TCN.ease}`,
                    }}
                    onFocus={(e) => { e.target.style.borderColor = TCN.cyan; }}
                    onBlur={(e) => { e.target.style.borderColor = TCN.border; }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 11,
                      fontWeight: 600,
                      color: TCN.textDim,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    {t('login.passwordLabel', 'Password')}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      style={{
                        width: '100%',
                        padding: '11px 44px 11px 14px',
                        borderRadius: 12,
                        background: TCN.surface2,
                        border: `1px solid ${TCN.border}`,
                        color: TCN.text,
                        // FRM-001/TCH-001 — 16px : pas de zoom iOS au focus.
                        fontSize: 16,
                        fontFamily: TCN.fontSans,
                        outline: 'none',
                        transition: `border-color ${TCN.durBase} ${TCN.ease}`,
                      }}
                      onFocus={(e) => { e.target.style.borderColor = TCN.cyan; }}
                      onBlur={(e) => { e.target.style.borderColor = TCN.border; }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute',
                        right: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        color: TCN.textDim,
                        cursor: 'pointer',
                        padding: 4,
                      }}
                    >
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                </div>

                <CTAButton type="submit" variant="primary" size="md" loading={loading}>
                  {loading
                    ? t('login.signingIn', 'Signing in…')
                    : t('login.signIn', 'Sign in')}
                </CTAButton>

                <p
                  style={{
                    fontSize: 11,
                    color: TCN.textMute,
                    textAlign: 'center',
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {t(
                    'tcn.login.contactAdmin',
                    'Trouble signing in? Contact your TCN admin.'
                  )}
                </p>
              </form>
            </div>

            <div
              className="flex items-center justify-center gap-4 mt-6"
              style={{ fontSize: 11, color: TCN.textMute }}
            >
              <Link
                to="/terms"
                style={{ color: TCN.textMute, textDecoration: 'none' }}
              >
                {t('login.terms', 'Terms')}
              </Link>
              <span>·</span>
              <Link
                to="/privacy"
                style={{ color: TCN.textMute, textDecoration: 'none' }}
              >
                {t('login.privacy', 'Privacy')}
              </Link>
            </div>
            <p
              style={{
                fontSize: 11,
                color: TCN.textMute,
                textAlign: 'center',
                marginTop: 8,
                opacity: 0.7,
              }}
            >
              © 2026 NoiRock · TCN
            </p>
          </div>
        </main>
      </div>
    </TcnThemeProvider>
  );
};

export default TcnLogin;
